import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Users,
  Shield,
  FileText,
  Settings,
  Loader2,
  Brain,
  Mail,
  Tag,
  Package,
  Activity,
  UserCircle,
} from 'lucide-react';
import { toast } from 'sonner';

// Import tab components
import UsersTab from './UsersTab';
import PackagesTab from './PackagesTab';
import ClientCategoriesTab from './ClientCategoriesTab';
import ClientPortalTab from './ClientPortalTab';
import EmailTemplatesTab from './EmailTemplatesTab';
import TacoBrainTab from './TacoBrainTab';
import TacoHealthTab from './TacoHealthTab';
import AuditLogsTab from './AuditLogsTab';
import SettingsTab from './SettingsTab';

const AdminPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditActions, setAuditActions] = useState([]);
  const [alfredSettings, setAlfredSettings] = useState(null);
  const [appSettings, setAppSettings] = useState(null);
  const [tacoStats, setTacoStats] = useState(null);
  const [tacoMemories, setTacoMemories] = useState([]);
  const [clientCategories, setClientCategories] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, logsRes, actionsRes, alfredRes, settingsRes, tacoStatsRes, tacoMemoriesRes, categoriesRes, templatesRes, packagesRes] = await Promise.all([
        api().get('/users'),
        api().get('/admin/audit-logs'),
        api().get('/admin/audit-logs/actions'),
        api().get('/alfred/settings'),
        api().get('/admin/settings'),
        api().get('/admin/taco/stats'),
        api().get('/admin/taco/memories'),
        api().get('/admin/client-categories'),
        api().get('/admin/email-templates'),
        api().get('/admin/service-packages'),
      ]);
      setUsers(usersRes.data);
      setAuditLogs(logsRes.data.logs || logsRes.data);
      setAuditActions(actionsRes.data.actions || []);
      setAlfredSettings(alfredRes.data);
      setAppSettings(settingsRes.data);
      setTacoStats(tacoStatsRes.data);
      setTacoMemories(tacoMemoriesRes.data.memories || []);
      setClientCategories(categoriesRes.data || []);
      setEmailTemplates(templatesRes.data || []);
      setServicePackages(packagesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">Admin Portal</h1>
        <p className="text-zinc-500 mt-1">Manage users, roles, and system settings</p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:grid-cols-9">
          <TabsTrigger value="users" data-testid="users-tab">
            <Users className="mr-2 h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="client-portal" data-testid="client-portal-tab">
            <UserCircle className="mr-2 h-4 w-4" />
            Portal
          </TabsTrigger>
          <TabsTrigger value="packages" data-testid="packages-tab">
            <Package className="mr-2 h-4 w-4" />
            Packages
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="categories-tab">
            <Tag className="mr-2 h-4 w-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="email-templates" data-testid="email-templates-tab">
            <Mail className="mr-2 h-4 w-4" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="taco-brain" data-testid="taco-brain-tab">
            <Brain className="mr-2 h-4 w-4" />
            Taco Brain
          </TabsTrigger>
          <TabsTrigger value="taco-health" data-testid="taco-health-tab">
            <Activity className="mr-2 h-4 w-4" />
            Taco Health
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab">
            <FileText className="mr-2 h-4 w-4" />
            Audit
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="settings-tab">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <UsersTab 
            api={api} 
            currentUser={currentUser} 
            users={users} 
            fetchData={fetchData} 
          />
        </TabsContent>

        {/* Client Portal Tab */}
        <TabsContent value="client-portal" className="space-y-4">
          <ClientPortalTab api={api} />
        </TabsContent>

        {/* Service Packages Tab */}
        <TabsContent value="packages" className="space-y-4">
          <PackagesTab 
            api={api} 
            servicePackages={servicePackages} 
            fetchData={fetchData} 
          />
        </TabsContent>

        {/* Client Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <ClientCategoriesTab 
            api={api} 
            clientCategories={clientCategories} 
            fetchData={fetchData} 
          />
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="email-templates" className="space-y-4">
          <EmailTemplatesTab 
            api={api} 
            emailTemplates={emailTemplates} 
            fetchData={fetchData} 
          />
        </TabsContent>

        {/* Taco Brain Tab */}
        <TabsContent value="taco-brain" className="space-y-6">
          <TacoBrainTab 
            api={api} 
            tacoStats={tacoStats} 
            tacoMemories={tacoMemories} 
            fetchData={fetchData}
            fetchTacoMemories={setTacoMemories}
          />
        </TabsContent>

        {/* Taco Health Tab */}
        <TabsContent value="taco-health" className="space-y-6">
          <TacoHealthTab api={api} />
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="space-y-4">
          <AuditLogsTab 
            api={api} 
            users={users} 
            auditLogs={auditLogs} 
            auditActions={auditActions}
            setAuditLogs={setAuditLogs}
          />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <SettingsTab 
            api={api} 
            appSettings={appSettings} 
            setAppSettings={setAppSettings}
            alfredSettings={alfredSettings}
            fetchData={fetchData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
